import React from 'react';
import { Chip } from '@jotpsych/ui';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import type { CleanupTypeVersion } from '../types';
import { lastUsedLabel } from '../utils/versionSignals';

export const STALE_WEEK_OPTIONS = [8, 12, 26] as const;

export interface Step1ArchivedReviewProps {
  archived: CleanupTypeVersion[];
  /** Ids the admin chose to rescue. */
  restoredIds: ReadonlySet<string>;
  onToggleRestore: (id: string) => void;
  /** Stale-archive tool: solo stale types unused ≥ staleWeeks. */
  staleCandidates?: CleanupTypeVersion[];
  /** Candidates Layer 1 would auto-share — flagged so the admin sees why. */
  autoPromoteBoundIds?: ReadonlySet<string>;
  staleWeeks?: number;
  onStaleWeeksChange?: (weeks: number) => void;
  /** Candidates the admin UNchecked (default is archive-all-candidates). */
  staleUncheckedIds?: ReadonlySet<string>;
  onToggleStaleArchive?: (id: string) => void;
  disabled?: boolean;
}

/**
 * Wizard Step 1 (GDN-2236): safety-net review of what Layer 1 auto-archived.
 * Archiving already happened (it needs no judgment) — the admin skims and
 * rescues anything they still want. Dense single-line rows: every entry is
 * the same shape ("never used"), so the list should read like a receipt,
 * not a decision. Pure presentation.
 */
export const Step1ArchivedReview: React.FC<Step1ArchivedReviewProps> = ({
  archived,
  restoredIds,
  onToggleRestore,
  staleCandidates = [],
  autoPromoteBoundIds = new Set<string>(),
  staleWeeks = 12,
  onStaleWeeksChange,
  staleUncheckedIds = new Set<string>(),
  onToggleStaleArchive,
  disabled = false,
}) => (
  <Box>
    <Typography variant="h6" sx={{ mb: 0.5 }}>
      {archived.length} unused types were archived
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mb: 2, maxWidth: 640 }}
    >
      These hadn&apos;t been used, so we cleared them from everyone&apos;s
      scheduling picker. They still appear in past notes and appointments —
      they just won&apos;t clutter new ones. Restore any you still want.
    </Typography>

    <Stack spacing={0}>
      {archived.map((type, index) => {
        const restored = restoredIds.has(type.id);
        return (
          <Box
            key={type.id}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom:
                index < archived.length - 1
                  ? `1px solid ${theme.palette.divider}`
                  : 'none',
              px: 1,
              py: 0.5,
              opacity: restored ? 1 : 0.8,
              '&:hover': { bgcolor: theme.palette.action.hover },
            })}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, minWidth: 0 }}
              noWrap
            >
              {type.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {type.provider ?? 'Unassigned'} · never used
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {restored && (
                <Chip size="xSmall" variant="soft" color="success" label="restored" />
              )}
              <Button
                size="small"
                variant="text"
                color={restored ? 'inherit' : 'primary'}
                disabled={disabled}
                onClick={() => onToggleRestore(type.id)}
                sx={{ flexShrink: 0 }}
              >
                {restored ? 'Re-archive' : 'Restore'}
              </Button>
            </Box>
          </Box>
        );
      })}
    </Stack>

    {staleCandidates.length > 0 && (
      <Box sx={{ mt: 3 }}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}
        >
          <Typography variant="h6">
            {staleCandidates.filter((t) => !staleUncheckedIds.has(t.id)).length}{' '}
            stale types to archive too
          </Typography>
          <Select
            size="small"
            value={staleWeeks}
            disabled={disabled}
            onChange={(e) => onStaleWeeksChange?.(e.target.value)}
            sx={{ ml: 1 }}
          >
            {STALE_WEEK_OPTIONS.map((w) => (
              <MenuItem key={w} value={w}>
                unused ≥ {w} weeks
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5, maxWidth: 680 }}
        >
          Types that haven&apos;t been used in a while and that no later
          step will review — a provider&apos;s own types, plus a few that
          would otherwise be auto-shared into the catalog sight-unseen.
          Types with upcoming appointments never appear here. Archiving is
          reversible and keeps past notes intact — uncheck any that should
          stay.
        </Typography>

        <Stack spacing={0}>
          {staleCandidates.map((type, index) => {
            const willArchive = !staleUncheckedIds.has(type.id);
            return (
              <Box
                key={type.id}
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom:
                    index < staleCandidates.length - 1
                      ? `1px solid ${theme.palette.divider}`
                      : 'none',
                  px: 1,
                  py: 0.25,
                  bgcolor: willArchive
                    ? alpha(theme.palette.warning.main, 0.04)
                    : 'transparent',
                  '&:hover': { bgcolor: theme.palette.action.hover },
                })}
              >
                <Checkbox
                  size="small"
                  checked={willArchive}
                  disabled={disabled}
                  onChange={() => onToggleStaleArchive?.(type.id)}
                  sx={{ p: 0.25 }}
                  slotProps={{
                    input: { 'aria-label': `Archive ${type.name}` },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, minWidth: 0 }}
                  noWrap
                >
                  {type.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {type.provider ?? 'Unassigned'} · {type.usage} visits ·{' '}
                  {lastUsedLabel(type.lastUsedAt) ?? 'never used'}
                </Typography>
                {autoPromoteBoundIds.has(type.id) && (
                  <Chip
                    size="xSmall"
                    variant="soft"
                    color="warning"
                    label="was headed to the shared catalog"
                  />
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    )}
  </Box>
);
